/**
 * BeforeAfterKPI - Before/After comparison component for optimization KPIs.
 *
 * Props:
 *   - baselineKPIs: Object with baseline values (no optimization)
 *   - optimizedKPIs: Object with optimized values
 *
 * Displays a two-column comparison layout:
 *   - Left: Baseline (No Optimization) values
 *   - Right: Optimized values with % improvement badges
 *
 * KPIs shown:
 *   - Daily Cost (EUR) - lower is better
 *   - Self-Sufficiency (%) - higher is better
 *   - Self-Consumption (%) - higher is better
 *   - Grid Import (kWh) - lower is better
 *   - Grid Export Revenue (EUR) - higher is better
 *   - CO2 Avoided (kg) - higher is better
 *
 * Color coding: green for improvements, red for degradation
 */

import React from 'react'
import { Row, Col, Card } from 'react-bootstrap'

// Exchange rates to EUR (add new currencies here)
const EXCHANGE_RATES = { CZK: 25, EUR: 1, PLN: 4.3, HUF: 395, RON: 5, BGN: 1.96 }

function BeforeAfterKPI({ baselineKPIs, optimizedKPIs, currency = 'EUR' }) {
  const currSym = currency === 'EUR' ? '€' : currency
  const rate = EXCHANGE_RATES[currency] || 1  // Backend returns EUR; multiply to display in local currency
  // Mock data if not provided
  const baseline = baselineKPIs || {
    daily_cost_eur: 45.2,
    self_sufficiency_pct: 35,
    self_consumption_pct: 45,
    grid_import_kwh: 85,
    grid_export_revenue_eur: 2.1,
    co2_avoided_kg: 0,
  }

  const optimized = optimizedKPIs || {
    daily_cost_eur: 32.8,
    self_sufficiency_pct: 62,
    self_consumption_pct: 78,
    grid_import_kwh: 52,
    grid_export_revenue_eur: 5.4,
    co2_avoided_kg: 18.5,
  }

  // Calculate percentage improvements
  const calculateImprovement = (optimizedVal, baselineVal, higherIsBetter = true) => {
    if (baselineVal === 0) return 0
    const change = ((optimizedVal - baselineVal) / baselineVal) * 100
    return higherIsBetter ? change : -change
  }

  const kpis = [
    {
      label: 'Daily Cost',
      unit: ` ${currSym}`,
      baselineValue: baseline.daily_cost_eur * rate,
      optimizedValue: optimized.daily_cost_eur * rate,
      higherIsBetter: false,
      icon: '💰',
    },
    {
      label: 'Self-Sufficiency',
      unit: '%',
      baselineValue: baseline.self_sufficiency_pct,
      optimizedValue: optimized.self_sufficiency_pct,
      higherIsBetter: true,
      icon: '⚡',
    },
    {
      label: 'Self-Consumption',
      unit: '%',
      baselineValue: baseline.self_consumption_pct,
      optimizedValue: optimized.self_consumption_pct,
      higherIsBetter: true,
      icon: '🔄',
    },
    {
      label: 'Grid Import',
      unit: ' kWh',
      baselineValue: baseline.grid_import_kwh,
      optimizedValue: optimized.grid_import_kwh,
      higherIsBetter: false,
      icon: '🔌',
    },
    {
      label: 'Potential Export Revenue',
      unit: ` ${currSym}`,
      baselineValue: baseline.grid_export_revenue_eur * rate,
      optimizedValue: optimized.grid_export_revenue_eur * rate,
      higherIsBetter: true,
      icon: '💵',
    },
    {
      label: 'CO2 Avoided',
      unit: ' kg',
      baselineValue: baseline.co2_avoided_kg,
      optimizedValue: optimized.co2_avoided_kg,
      higherIsBetter: true,
      icon: '🌱',
    },
  ]

  return (
    <div className="kpi-comparison">
      {/* Header */}
      <div className="text-center mb-4">
        <h5 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600, color: 'var(--color-neutral-900)' }}>
          Before / After Optimization Comparison
        </h5>
        <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral-600)', margin: 0 }}>
          Baseline: direct PV-to-load only (no battery/flex) vs Optimized: MILP-scheduled battery and flex assets
        </p>
      </div>

      {/* KPI Grid */}
      <Row className="g-3">
        {kpis.map((kpi) => {
          const improvement = calculateImprovement(
            kpi.optimizedValue,
            kpi.baselineValue,
            kpi.higherIsBetter
          )
          const isImprovement = improvement > 0
          const improvementColor = isImprovement ? 'var(--color-success)' : 'var(--color-danger)'
          const improvementBg = isImprovement ? '#D1F2EB' : '#F8D7DA'

          return (
            <Col key={kpi.label} sm={6} lg={4}>
              <Card
                style={{
                  border: '1px solid var(--color-neutral-200)',
                  borderRadius: 'var(--radius-lg)',
                  overflow: 'hidden',
                }}
              >
                <Card.Body className="p-3">
                  {/* Icon and Label */}
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <span style={{ fontSize: '1.2rem' }}>{kpi.icon}</span>
                    <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500, color: 'var(--color-neutral-700)' }}>
                      {kpi.label}
                    </div>
                  </div>

                  {/* Baseline Value */}
                  <div className="mb-2">
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)' }}>
                      Baseline (No Battery/Flex)
                    </div>
                    <div style={{ fontSize: 'var(--font-size-base)', color: 'var(--color-neutral-600)' }}>
                      {kpi.baselineValue.toFixed(kpi.unit === '%' ? 1 : 2)}
                      {kpi.unit}
                    </div>
                  </div>

                  {/* Optimized Value */}
                  <div className="mb-2">
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)' }}>
                      Optimized
                    </div>
                    <div
                      style={{
                        fontSize: 'var(--font-size-xl)',
                        fontWeight: 700,
                        color: 'var(--color-primary-dark)',
                      }}
                    >
                      {kpi.optimizedValue.toFixed(kpi.unit === '%' ? 1 : 2)}
                      {kpi.unit}
                    </div>
                  </div>

                  {/* Improvement Badge */}
                  <div
                    style={{
                      display: 'inline-block',
                      backgroundColor: improvementBg,
                      color: improvementColor,
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      padding: 'var(--space-1) var(--space-3)',
                      borderRadius: 'var(--radius-full)',
                    }}
                  >
                    {isImprovement ? '▲' : '▼'} {Math.abs(improvement).toFixed(1)}%
                  </div>
                </Card.Body>
              </Card>
            </Col>
          )
        })}
      </Row>
    </div>
  )
}

export default BeforeAfterKPI
